'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useStore } from 'jotai'
import { ArrowDownIcon, ArrowUpIcon, ChevronLeft, LoaderCircle, Search, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type {
  Dispatch,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  SetStateAction,
} from 'react'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { OverlayScrollY, OverlayThumbY } from '@/components/widget/overlay-scroll-y'
import {
  type CappedWizardAsset,
  collectAssetClasses,
  filterAssetsByClass,
  filterAssetsByQuery,
  groupByAsset,
  matchesBaseAsset,
  sortAssets,
  type WizardAsset,
} from '@/features/dashboard/components/watchlist-wizard/asset-grouping'
import { buildAssetSections } from '@/features/dashboard/components/watchlist-wizard/asset-sections'
import { useHoverIntent } from '@/features/dashboard/components/watchlist-wizard/use-hover-intent'
import { WatchlistDetailPanel } from '@/features/dashboard/components/watchlist-wizard/watchlist-detail-panel'
import { WatchlistGroupSelectButton } from '@/features/dashboard/components/watchlist-wizard/watchlist-group-select-button'
import {
  WatchlistMasterRow,
  WatchlistSectionHeader,
  WatchlistSeeAllRow,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-master-row'
import {
  FilterMenu,
  FilterRow,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-filter-controls'
import {
  compareInstrumentsForDisplay,
  computeDefaultCollapsedOptionSections,
  type DetailRow,
  groupByMarketType,
  groupOptionsByExpiry,
  groupOptionsByVenueQuote,
  groupSelectionState,
  isFuturesOptionChain,
  marketTypeOf,
  representativeInstrument,
  representativeOptionInstrument,
  selectableAssetInstruments,
  sortOptionsVenueGroupsForDisplay,
  wizardGroupKey,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-grouping'
import {
  EnterKeyLabel,
  EscapeKeyLabel,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-key-labels'
import {
  WatchlistWizardSearchBar,
  type WatchlistWizardSearchBarHandle,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-search-bar'
import { WatchlistWizardSidePanel } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-side-panel'
import { WATCHLIST_WIZARD_KEY_HINT as KEY_HINT_CLASS } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-styles'
import {
  buildWizardViews,
  matchWizardView,
  type WizardView,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-views'
import { useWatchlistLiveTickers } from '@/features/dashboard/hooks/use-watchlist-live-ticker'
import {
  CATALOG_FETCHABLE_EXCHANGE_LIST,
  SUPPORTED_EXCHANGE_LIST,
  SUPPORTED_EXCHANGES as WIZARD_EXCHANGES,
} from '@/features/dashboard/lib/supported-exchanges'
import { watchlistEntryKey, watchlistEntrySet } from '@/features/dashboard/lib/watchlist-entry'
import { wizardPriceStore } from '@/features/dashboard/lib/wizard-price-store'
import { useDebounce } from '@/hooks/use-debounce'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { toast } from '@/lib/toast'
import { cn, safeParseFloat } from '@/lib/utils'
import { useMasterInstrumentLists } from '@/service/master-instrument.hook'
import { useEnabledExchangeList, useVenuesLoaded } from '@/service/venue.hook'
import { useSetWatchlist } from '@/service/watchlist.hook'
import { type ChartCompareMode, TV_COMPARE_MODES } from '@/state/chart-compare'
import { watchListTickerMapAtom } from '@/state/dashboard'
import { AssetClass, Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'
import type { WatchlistEntry } from '@/types/generated/public_api'

const EMPTY_ENTRIES: WatchlistEntry[] = []
const EMPTY_ICON_KEYS: string[] = []
const EMPTY_ENTRY_KEYS = new Set<string>()

export type MarketType = 'perpetual' | 'spot' | 'future' | 'options'
type Pane = 'master' | 'detail'

interface CollapsedDetailState {
  assetId: string | null
  sections: Set<string>
}

interface MasterVisibleRange {
  startIndex: number
  endIndex: number
}

// A rough first-screen guess so the master list's ticker subscription
// (below) doesn't wait for Virtuoso's first `rangeChanged` callback — the
// dialog can render up to ~15 rows on the tallest supported viewport.
const INITIAL_MASTER_VISIBLE_RANGE: MasterVisibleRange = { startIndex: 0, endIndex: 19 }
// Small scroll-ahead pad so a fast scroll's price appears without waiting on
// a fresh subscription — deliberately smaller than the detail pane's
// WIZARD_PRICE_RANGE_BUFFER since these come from an already-live map
// (no network round trip to hide), not a fresh WS subscribe.
const MASTER_TICKER_RANGE_BUFFER = 8

// Per-row entrance stagger for the master list — replayed (via the
// `resultVersion`-keyed wrapper below) whenever a query/filter change
// produces a new result set, instead of the whole list just popping in at
// once. Capped so a long result set doesn't make the last visible row wait
// nearly a second for its turn.
const MASTER_ROW_STAGGER_DELAY_S = 0.025
const MASTER_ROW_STAGGER_MAX_INDEX = 10

// Below this many rows, the master list renders as a plain (non-virtualized)
// animated list instead of through Virtuoso — small enough that mounting
// every row as real DOM is cheap, and it's what makes a true FLIP reorder
// animation possible (e.g. typing "hyp" sliding HYPE from row 4 to row 1):
// Virtuoso repositions virtualized rows by snapping a transform instantly,
// which gives Framer Motion's `layout` prop no continuous position to
// animate from. Above this count (the unfiltered/browse-all catalog),
// Virtuoso stays in charge and rows get the stagger fade-in below instead.
const MASTER_LIST_ANIMATE_THRESHOLD = 40

// Left pane holds the assets, right pane the venues of whichever asset the
// cursor is on. Their picker ships 56/44; ours stays draggable between these
// bounds because our right pane also has to hold option chains.
const DEFAULT_SPLIT_PERCENT = 56
const MIN_SPLIT_PERCENT = 34
const MAX_SPLIT_PERCENT = 70

// UI-config only (translated-label fallback + chip ordering) — the grouping
// order itself lives with the grouping functions in watchlist-wizard-grouping.ts.
const MARKET_TYPE_ORDER: { key: MarketType; label: string }[] = [
  { key: 'perpetual', label: 'Perp' },
  { key: 'spot', label: 'Spot' },
  { key: 'future', label: 'Futures' },
  { key: 'options', label: 'Options' },
]

const ASSET_CLASS_TRANSLATION_KEYS: Partial<Record<AssetClass, string>> = {
  [AssetClass.ASSET_CLASS_CRYPTO]: 'crypto',
  [AssetClass.ASSET_CLASS_EQUITY]: 'equity',
  [AssetClass.ASSET_CLASS_COMMODITY]: 'commodity',
  [AssetClass.ASSET_CLASS_FX]: 'fx',
  [AssetClass.ASSET_CLASS_INDEX]: 'index',
  [AssetClass.ASSET_CLASS_RATE]: 'rate',
}

interface WatchlistWizardBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Forces the market-type filter to this value and hides the Type filter
   * entirely — for pickers that only ever want one type (e.g. an options-only
   * underlying picker), where a togglable *default* isn't enough since the user
   * could filter it away. Takes priority over `initialTypeFilter`. */
  lockedTypeFilter?: MarketType
  /** Restricts the catalog to these market types and offers only them in the Type
   * filter (which stays togglable among them) — e.g. a derivatives-only picker drops
   * spot. Instruments with no market type (index feeds) are dropped too. */
  allowedTypes?: readonly MarketType[]
  /** For pickers that only care about the underlying asset, not a specific
   * contract (e.g. picking a ticker for an analytics widget) — single mode
   * only. The venue pane lists exchanges rather than contracts, and picking
   * one immediately selects that exchange's representative instrument. */
  assetOnlySelection?: boolean
  /** Restricts the catalog and venue controls to one exchange. Useful when a
   * selected instrument must belong to an account-scoped venue. */
  lockedExchangeFilter?: Exchange
  /** Single mode: the caller needs one exact tradeable contract (RFQ, spreader,
   * arb legs), so the venue pane lists every option contract instead of one
   * representative per chain. (Selecting an asset always opens its contract
   * list now, whatever this is set to.) */
  detailSelectionOnly?: boolean
  /** Multi-select only. By default the Options section collapses each
   * venue+quote chain to one representative contract (nearest expiry,
   * strike closest to the live underlying price) — enough for casually
   * adding "the options market" to a watchlist without flooding it with
   * every strike/expiry. Set this when the picker's whole purpose is
   * precise option bookkeeping (e.g. the watchlist's own "+ Add" button) so
   * the user can drill into and pick an exact contract instead. */
  expandOptionChain?: boolean
  /** Search text to start with each time the dialog opens (e.g. letters typed on a chart). */
  initialQuery?: string
  /** Base asset to filter by each time the dialog opens (e.g. "BTC" — only markets whose base
   * is BTC, so a BTC-quoted pair like BTC-BLAST stays out). Shown as a removable pill. */
  initialBaseFilter?: string
}

interface WatchlistWizardMultiProps extends WatchlistWizardBaseProps {
  /** Checkbox multi-select, batched into a server watchlist via a confirm button. */
  mode?: 'multi'
  watchlistId: string
  existingEntries: WatchlistEntry[]
  /** Pre-checks this type's filter each time the dialog opens — e.g. opening
   * "Add" from within the watchlist's Perps tab should default to just Perps. */
  initialTypeFilter?: MarketType
}

interface WatchlistWizardSingleProps extends WatchlistWizardBaseProps {
  /** Click-to-select: picking a row immediately fires onSelect and closes the dialog. */
  mode: 'single'
  existingEntries?: WatchlistEntry[]
  onSelect: (instrument: Instrument, lastPrice?: string) => void
  /** Opened from a chart: shows the side panel's "Compare on chart" section.
   * While a compare mode is picked there, choosing a market calls `onCompare`
   * instead of `onSelect`. `requireMode` drops the "None" option — for the
   * chart's own Compare picker, where a pick can only ever mean a compare.
   * `modes` lists the chart engine's own modes (default: the TV chart's). */
  chartCompare?: {
    onCompare: (instrument: Instrument, mode: ChartCompareMode) => void
    requireMode?: boolean
    modes?: ChartCompareMode[]
  }
}

export type WatchlistWizardProps = WatchlistWizardMultiProps | WatchlistWizardSingleProps

export function WatchlistWizard(props: WatchlistWizardProps) {
  // Read ticker snapshots only at the interaction points that need them.
  // Visible price rows subscribe to their own symbol, so unrelated ticks do
  // not re-render this entire dialog.
  const store = useStore()

  // 2. State, refs, derived state, and constants
  const t = useTranslations('watchlistWizard')
  const { open, onOpenChange } = props
  const isSingleMode = props.mode === 'single'
  const onSelect = props.mode === 'single' ? props.onSelect : null
  // Collapsed to one representative contract per venue+quote chain by
  // default everywhere — including a hovered/previewed asset's detail pane,
  // not just an actually-committed selection. A caller opts out only when
  // its whole purpose is resolving to one exact leaf contract: single-mode
  // `detailSelectionOnly` (RFQ, spreader — an exact tradeable instrument is
  // required) or multi-mode `expandOptionChain` (the watchlist's own
  // "+ Add", a deliberate options-management flow). Every other caller —
  // e.g. the header "+" new-chart-tab picker — gets the collapsed view.
  const showCollapsedOptionChain =
    !props.detailSelectionOnly && !(props.mode !== 'single' && props.expandOptionChain)
  const existingEntries = props.existingEntries ?? EMPTY_ENTRIES
  const [query, setQuery] = useState('')
  // The one integer the whole dialog turns on: hover and the arrow keys both
  // write it, the venue pane and Enter both read it. Two input methods that
  // share a cursor can never disagree about what is selected.
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [activePane, setActivePane] = useState<Pane>('master')
  // Bumped whenever the user steps into the venue pane (not by a hover preview) —
  // WatchlistDetailPanel staggers its rows in on each bump.
  const [detailEntryKey, setDetailEntryKey] = useState(0)
  const [detailIndex, setDetailIndex] = useState(-1)
  // Master list's ticker subscription only ever needs whatever's actually on
  // screen (+ a small pad) — see the batched useWatchlistLiveTickers call below.
  const [masterVisibleRange, setMasterVisibleRange] = useState<MasterVisibleRange>(
    INITIAL_MASTER_VISIBLE_RANGE
  )
  // Which instrument-kind sections (Perpetuals/Spot/Futures/Options-expiry) are
  // collapsed within the venue pane's Virtuoso list — keyed together with
  // the asset so a newly hovered asset can use its defaults synchronously.
  const [collapsedDetailState, setCollapsedDetailState] = useState<CollapsedDetailState>(() => ({
    assetId: null,
    sections: new Set(),
  }))
  // Multi-select mode edits a draft of the complete watchlist. Starting with
  // the existing entries selected lets an already-added row be clicked again
  // to remove it from the draft, then restored with another click.
  const [selectedEntries, setSelectedEntries] = useState<WatchlistEntry[]>(() =>
    isSingleMode ? [] : existingEntries
  )
  const [exchangeFilters, setExchangeFilters] = useState<Set<Exchange>>(() => new Set())
  const [typeFilters, setTypeFilters] = useState<Set<MarketType>>(() => new Set())
  // Single-select, unlike the other two. Three ANDed multi-selects produce
  // empty states the user can't attribute to any one chip; a radio can't
  // reach that state at all.
  const [assetClassFilter, setAssetClassFilter] = useState<AssetClass | null>(null)
  // Single-select: a market has exactly one quote, so ORing several would
  // only ever read as "Any quote" with extra steps.
  const [quoteFilter, setQuoteFilter] = useState<string | null>(null)
  const [baseFilter, setBaseFilter] = useState<string | null>(null)
  const chartCompare = props.mode === 'single' ? props.chartCompare : undefined
  const compareRequiresMode = Boolean(chartCompare?.requireMode)
  const compareModes = chartCompare?.modes ?? TV_COMPARE_MODES
  const [compareMode, setCompareMode] = useState<ChartCompareMode | null>(null)
  // True while the search bar is mid-`@token` — see WatchlistWizardSearchBar's
  // onActiveTokenChange for why the capture-phase handler below needs this.
  const [atTokenActive, setAtTokenActive] = useState(false)
  const [contentReady, setContentReady] = useState(false)
  const [splitPercent, setSplitPercent] = useState(DEFAULT_SPLIT_PERCENT)
  const [isSplitDragging, setIsSplitDragging] = useState(false)
  // Bumped whenever the (debounced) search/filters actually produce a new
  // result set — see the effect below. Used purely as a `key` on each visible
  // row's animation wrapper so the whole on-screen set staggers back in
  // together, even for a row whose underlying asset.id (and therefore its
  // Virtuoso item identity) didn't change, just its position.
  const [resultVersion, setResultVersion] = useState(0)
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const [masterScroller, setMasterScroller] = useState<HTMLElement | null>(null)
  const detailVirtuosoRef = useRef<VirtuosoHandle>(null)
  const listContainerRef = useRef<HTMLDivElement>(null)
  const masterPaneRef = useRef<HTMLElement>(null)
  const detailPaneRef = useRef<HTMLElement>(null)
  const splitPercentRef = useRef(DEFAULT_SPLIT_PERCENT)
  const splitDraggingRef = useRef(false)
  const searchBarRef = useRef<WatchlistWizardSearchBarHandle>(null)
  // Holds the latest keydown handler closure — see the assignment further
  // down and the effect that reads it, right before the JSX return.
  const handleKeyDownRef = useRef<(event: KeyboardEvent) => void>(() => {})
  // A real debounce (not useDeferredValue, which still runs the full
  // derivation on every keystroke — just at lower scheduling priority) so
  // fast typing doesn't pay for a recompute + re-render per character.
  // While an `@token` is being composed, its literal text (e.g. "@Hy") is
  // never a real instrument search string — blank it out here, before
  // debouncing, so results never flash "no matches" for it mid-composition
  // or in the brief window right after committing (when `atTokenActive`
  // has already gone false but the debounce hasn't caught up to the
  // now-empty query yet).
  const { value: debouncedQuery } = useDebounce(atTokenActive ? '' : query, 120)
  const normalizedQuery = debouncedQuery.trim().toLowerCase()
  const isSearching = normalizedQuery.length > 0
  const { mutate: setWatchlist, isPending } = useSetWatchlist()
  const { assetOnlySelection, lockedExchangeFilter } = props
  // Filtered by GetVenues' `enabled` (server-driven) and CATALOG_FETCHABLE_EXCHANGE_LIST's
  // static `enabled` (manual override — see its doc comment) so a disabled venue's
  // instrument catalog doesn't get polled forever.
  const enabledWizardExchanges = useEnabledExchangeList(CATALOG_FETCHABLE_EXCHANGE_LIST)
  const catalogExchanges = useMemo(
    () =>
      lockedExchangeFilter === undefined
        ? enabledWizardExchanges
        : enabledWizardExchanges.filter((exchange) => exchange === lockedExchangeFilter),
    [enabledWizardExchanges, lockedExchangeFilter]
  )
  // Also gated on GetVenues having answered (not just `open && contentReady`)
  // — until then, `catalogExchanges` optimistically includes every exchange
  // (see `useVenuesLoaded`), so a wizard opened before the dashboard-level
  // prefetch's GetVenues call resolves would send a doomed request for any
  // venue about to come back disabled (e.g. Binance).
  const venuesLoaded = useVenuesLoaded()
  const { instruments, isLoading } = useMasterInstrumentLists(
    catalogExchanges,
    open && contentReady && venuesLoaded
  )

  const { lockedTypeFilter, allowedTypes } = props
  const initialTypeFilter = lockedTypeFilter ?? (isSingleMode ? undefined : props.initialTypeFilter)
  // Exchange/Type/Asset-class filters apply everywhere (both the browse list
  // and search results), not just while searching. Also drops instruments
  // with no symbol — some venues' funding/index feeds decode as a real
  // Instrument (kind FUTURE, a funding interval, so isPerpInstrument still
  // classifies them as a perp) but have nothing a user could actually select,
  // and used to show up as blank, unlabeled rows in the venue pane.
  const filteredInstruments = useMemo(() => {
    return instruments.filter((instrument) => {
      if (!instrument.symbol) return false
      if (lockedExchangeFilter !== undefined && instrument.exchange !== lockedExchangeFilter) {
        return false
      }
      if (allowedTypes) {
        const type = marketTypeOf(instrument)
        if (!type || !allowedTypes.includes(type)) return false
      }
      if (exchangeFilters.size > 0 && !exchangeFilters.has(instrument.exchange)) return false
      if (typeFilters.size > 0) {
        const type = marketTypeOf(instrument)
        if (!type || !typeFilters.has(type)) return false
      }
      if (quoteFilter !== null && instrument.quoteCurrency.toUpperCase() !== quoteFilter) {
        return false
      }
      if (baseFilter !== null && !matchesBaseAsset(instrument, baseFilter)) {
        return false
      }
      return true
    })
  }, [
    instruments,
    exchangeFilters,
    typeFilters,
    quoteFilter,
    baseFilter,
    lockedExchangeFilter,
    allowedTypes,
  ])

  // Browse order: the curated majors, then Deribit availability, then volume.
  // Search replaces this wholesale — see `searchedAssets`.
  // One left-pane row per base ticker — even for an asset whose options trade
  // in more than one quote currency (e.g. Deribit's BTC options settle in
  // both USD and USDC). Those quote variants are reachable from the venue
  // pane's "Deribit BTC" / "Deribit BTC_USDC" grouping instead of getting
  // their own row here.
  // Deliberately NOT keyed on `contentReady` — grouping+sorting the whole
  // catalog (options chains included) isn't cheap, and gating it on a
  // per-open flag forced a full recompute every time the dialog reopened even
  // when `filteredInstruments` hadn't changed since the last close. Keying
  // only on the actual input lets repeat opens reuse the memoized result
  // instantly; `contentReady` still governs the loading-spinner/fade-in below.
  const baseAssets = useMemo(
    () => sortAssets(filterAssetsByClass(groupByAsset(filteredInstruments), assetClassFilter)),
    [filteredInstruments, assetClassFilter]
  )

  const searchedAssets = useMemo(() => {
    if (!isSearching) return baseAssets
    return filterAssetsByQuery(baseAssets, normalizedQuery)
  }, [baseAssets, isSearching, normalizedQuery])

  // Section capping is presentation; the count is fact. This is read before
  // capping, so the footer truthfully says "151 results" while twelve rows
  // are on screen.
  const resultCount = searchedAssets.length

  const sections = useMemo(() => buildAssetSections(searchedAssets), [searchedAssets])
  const masterAssets = sections.assets
  const isMasterListAnimated =
    masterAssets.length > 0 && masterAssets.length <= MASTER_LIST_ANIMATE_THRESHOLD

  // One shared subscription for whatever's actually on screen (+ a small
  // pad), instead of every master row mounting its own `watchListTickerMapAtom`
  // subscription — see useWatchlistLiveTickers' doc comment for why that
  // mattered (every tick of ANY watchlist symbol, wizard-relevant or not,
  // used to re-evaluate one selector per on-screen row for as long as the
  // list stayed mounted).
  const masterTickerKeys = useMemo(() => {
    const start = Math.max(0, masterVisibleRange.startIndex - MASTER_TICKER_RANGE_BUFFER)
    const end = Math.min(
      masterAssets.length - 1,
      masterVisibleRange.endIndex + MASTER_TICKER_RANGE_BUFFER
    )
    const keys: string[] = []
    for (let i = start; i <= end; i++) {
      const asset = masterAssets[i]
      if (!asset) continue
      const representative = representativeInstrument(asset.instruments)
      if (representative) keys.push(`${representative.exchange}:${representative.symbol}`)
    }
    return keys
  }, [masterAssets, masterVisibleRange])
  const masterTickerMap = useWatchlistLiveTickers(masterTickerKeys)

  const availableExchanges = useMemo(() => {
    const instrumentExchanges = new Set(instruments.map(({ exchange }) => exchange))
    return WIZARD_EXCHANGES.filter(({ exchange }) => instrumentExchanges.has(exchange))
  }, [instruments])

  const availableAssetClasses = useMemo(() => collectAssetClasses(instruments), [instruments])

  // Most-listed quote first, so USDT/USD/USDC lead and one-off quotes sink.
  const quoteOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const { quoteCurrency } of instruments) {
      const quote = quoteCurrency.toUpperCase()
      if (quote) counts.set(quote, (counts.get(quote) ?? 0) + 1)
    }
    return [...counts.entries()]
      .toSorted((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
      .map(([quote]) => quote)
  }, [instruments])

  const savedViews = useMemo(
    () =>
      buildWizardViews({
        availableExchanges: availableExchanges.map(({ exchange }) => exchange),
        availableAssetClasses,
        exchangeLocked: lockedExchangeFilter !== undefined,
        typeLocked: lockedTypeFilter !== undefined,
      }),
    [availableAssetClasses, availableExchanges, lockedExchangeFilter, lockedTypeFilter]
  )
  // A locked type sits in typeFilters but isn't the user's choice — leave it
  // out so "All" still reads as active under a locked picker.
  const activeViewId = matchWizardView(savedViews, {
    exchanges: exchangeFilters,
    types: lockedTypeFilter ? new Set() : typeFilters,
    assetClass: assetClassFilter,
    quote: quoteFilter,
  })

  const clampedIndex =
    masterAssets.length === 0 ? -1 : Math.min(Math.max(selectedIndex, 0), masterAssets.length - 1)
  const selectedAsset: WizardAsset | null = clampedIndex < 0 ? null : masterAssets[clampedIndex]
  const selectedAssetId = selectedAsset?.id ?? null
  const defaultCollapsedDetailSections = useMemo(
    () =>
      selectedAsset
        ? computeDefaultCollapsedOptionSections(selectedAsset.instruments)
        : new Set<string>(),
    [selectedAsset]
  )
  // Use the new asset's defaults during the very first render after a hover.
  // Waiting for the synchronization effect would build the new option tree
  // once with the previous asset's collapse keys, then build it again.
  const collapsedDetailSections =
    collapsedDetailState.assetId === selectedAssetId
      ? collapsedDetailState.sections
      : defaultCollapsedDetailSections

  // A stand-in "underlying price" for the selected asset, used only to pick
  // the nearest-the-money strike for the collapsed options representative
  // rows below — sourced from whichever non-option instrument (perp, spot,
  // ...) already represents this asset elsewhere in the wizard.
  // The wizard's own (10s-batched) price cache: the underlying's price when the app-wide
  // ticker map doesn't have it, and each option chain's own underlying once its
  // representative contract has ticked — both feed the ATM strike pick below.
  const wizardPrices = useSyncExternalStore(
    wizardPriceStore.subscribe,
    wizardPriceStore.getSnapshot,
    wizardPriceStore.getSnapshot
  )
  const underlyingKey = useMemo(() => {
    if (!selectedAsset) return undefined
    const nonOptionInstruments = selectedAsset.instruments.filter(
      (instrument) => instrument.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION
    )
    const representative = representativeInstrument(nonOptionInstruments)
    return representative
      ? watchlistEntryKey(representative.exchange, representative.symbol)
      : undefined
  }, [selectedAsset])
  const underlyingPrice = useMemo(() => {
    if (!underlyingKey) return undefined
    const ticker = store.get(watchListTickerMapAtom)[underlyingKey]
    const raw =
      ticker?.lastPrice ||
      ticker?.markPrice ||
      ticker?.indexPrice ||
      wizardPrices[underlyingKey]?.price
    return raw ? safeParseFloat(raw) : undefined
  }, [store, underlyingKey, wizardPrices])

  const detailRows = useMemo<DetailRow[]>(() => {
    if (!selectedAsset || assetOnlySelection) return []
    const rows: DetailRow[] = []
    for (const typeGroup of groupByMarketType(selectedAsset.instruments)) {
      if (typeGroup.key === 'options') {
        const optionsSectionKey = typeGroup.key

        // Multi-select (watchlist add) usually never drills into individual
        // expiries/strikes — each venue+quote chain (e.g. "Deribit BTC",
        // "Deribit BTC_USDC") collapses to one representative contract
        // (nearest expiry, strike closest to the live underlying price), so
        // picking a row adds one real, valid contract instead of dumping the
        // whole chain on screen. `expandOptionChain` opts a specific picker
        // (the watchlist's own "+ Add") out of this. Single-select flows
        // that need to pick an exact contract (RFQ, spreader) keep the full
        // drill-down below regardless.
        if (showCollapsedOptionChain) {
          // "Bitcoin Options" rather than the specific contract symbol picked
          // under the hood — mirrors how the (now-removed) left-pane quote
          // variant shortcuts used to read.
          const optionsSublabel = `${selectedAsset.label} ${t(`marketTypes.${typeGroup.key}`)}`
          const futuresOptionsSublabel = `${selectedAsset.label} ${t('marketTypes.future')} ${t(`marketTypes.${typeGroup.key}`)}`
          const representativeRows = sortOptionsVenueGroupsForDisplay(
            groupOptionsByVenueQuote(typeGroup.instruments, selectedAsset.key)
          ).flatMap((venueGroup) => {
            let representative = representativeOptionInstrument(
              venueGroup.instruments,
              underlyingPrice
            )
            // The row shows this contract's IV as the chain's ATM IV, so re-pick the strike
            // against the chain's OWN underlying once it has ticked — the asset-level price
            // can be missing (median-strike guess) or off (a USDT/USDC or futures chain).
            const chainUnderlying = representative
              ? Number(
                  wizardPrices[watchlistEntryKey(representative.exchange, representative.symbol)]
                    ?.underlyingPrice
                )
              : NaN
            if (Number.isFinite(chainUnderlying) && chainUnderlying > 0) {
              representative = representativeOptionInstrument(
                venueGroup.instruments,
                chainUnderlying
              )
            }
            const sublabel = isFuturesOptionChain(venueGroup.instruments, selectedAsset.instruments)
              ? futuresOptionsSublabel
              : optionsSublabel
            return representative
              ? [{ representative, label: venueGroup.baseQuoteLabel, sublabel }]
              : []
          })
          rows.push({
            kind: 'section',
            key: optionsSectionKey,
            label: t(`marketTypes.${typeGroup.key}`),
            count: representativeRows.length,
            instruments: representativeRows.map((row) => row.representative),
          })
          if (collapsedDetailSections.has(optionsSectionKey)) continue
          for (const { representative, label, sublabel } of representativeRows) {
            rows.push({
              kind: 'instrument',
              key: watchlistEntryKey(representative.exchange, representative.symbol),
              instrument: representative,
              label,
              sublabel,
              showAtmIv: true,
            })
          }
          continue
        }

        // Three levels deep: options section -> venue/quote (e.g. "Deribit
        // BTC_USDC") -> expiry -> the leaf contracts, each independently
        // collapsible (collapse state is just tracked per row key, so nesting
        // depth doesn't need special handling there).
        rows.push({
          kind: 'section',
          key: optionsSectionKey,
          label: t(`marketTypes.${typeGroup.key}`),
          count: typeGroup.instruments.length,
          instruments: typeGroup.instruments,
        })
        if (collapsedDetailSections.has(optionsSectionKey)) continue

        for (const venueGroup of sortOptionsVenueGroupsForDisplay(
          groupOptionsByVenueQuote(typeGroup.instruments, selectedAsset.key)
        )) {
          const venueSectionKey = `${optionsSectionKey}:${venueGroup.key}`
          rows.push({
            kind: 'section',
            key: venueSectionKey,
            label: venueGroup.label,
            count: venueGroup.instruments.length,
            instruments: venueGroup.instruments,
            depth: 1,
          })
          if (collapsedDetailSections.has(venueSectionKey)) continue

          for (const expiryGroup of groupOptionsByExpiry(venueGroup.instruments)) {
            const expirySectionKey = `${venueSectionKey}:${expiryGroup.expiryMs}`
            rows.push({
              kind: 'section',
              key: expirySectionKey,
              label: expiryGroup.label,
              count: expiryGroup.instruments.length,
              instruments: expiryGroup.instruments,
              depth: 2,
            })
            if (collapsedDetailSections.has(expirySectionKey)) continue
            for (const instrument of expiryGroup.instruments.toSorted(
              compareInstrumentsForDisplay
            )) {
              rows.push({
                kind: 'instrument',
                key: watchlistEntryKey(instrument.exchange, instrument.symbol),
                instrument,
                indentation: 'expiry',
              })
            }
          }
        }
        continue
      }

      rows.push({
        kind: 'section',
        key: typeGroup.key,
        label: t(`marketTypes.${typeGroup.key}`),
        count: typeGroup.instruments.length,
        instruments: typeGroup.instruments,
      })
      if (collapsedDetailSections.has(typeGroup.key)) continue
      for (const instrument of typeGroup.instruments.toSorted(compareInstrumentsForDisplay)) {
        rows.push({
          kind: 'instrument',
          key: watchlistEntryKey(instrument.exchange, instrument.symbol),
          instrument,
        })
      }
    }
    return rows
  }, [
    assetOnlySelection,
    collapsedDetailSections,
    showCollapsedOptionChain,
    selectedAsset,
    t,
    underlyingPrice,
    wizardPrices,
  ])

  // Only concrete instruments are navigable — section headers are chrome, so
  // arrow-down never lands on one.
  const detailNavTargets = useMemo(
    () =>
      detailRows.flatMap((row, index) =>
        row.kind === 'instrument' ? [{ key: row.key, index, instrument: row.instrument }] : []
      ),
    [detailRows]
  )

  const clampedDetailIndex =
    detailIndex < 0 || detailNavTargets.length === 0
      ? -1
      : Math.min(detailIndex, detailNavTargets.length - 1)
  const highlightedKey =
    activePane === 'detail' ? (detailNavTargets[clampedDetailIndex]?.key ?? null) : null

  const existingKeys = useMemo(() => watchlistEntrySet(existingEntries), [existingEntries])
  const selectedKeys = useMemo(() => watchlistEntrySet(selectedEntries), [selectedEntries])
  const hasSelectionChanges =
    selectedEntries.length !== existingEntries.length ||
    selectedEntries.some(
      (entry) => !existingKeys.has(watchlistEntryKey(entry.exchange, entry.symbol))
    )

  const assetClassFilterSet = useMemo(
    () => (assetClassFilter === null ? new Set<AssetClass>() : new Set([assetClassFilter])),
    [assetClassFilter]
  )

  const searchBarTypeOptions = useMemo(
    () =>
      lockedTypeFilter
        ? []
        : MARKET_TYPE_ORDER.filter(({ key }) => !allowedTypes || allowedTypes.includes(key)).map(
            ({ key }) => ({ key, label: t(`marketTypeFilters.${key}`) })
          ),
    [allowedTypes, lockedTypeFilter, t]
  )

  const searchBarAssetClassOptions = useMemo(
    () =>
      availableAssetClasses.map((assetClass) => ({
        assetClass,
        label: t(`assetClasses.${ASSET_CLASS_TRANSLATION_KEYS[assetClass] ?? 'other'}`),
      })),
    [availableAssetClasses, t]
  )

  const localizedAssetClassLabel = useCallback(
    (assetClass: AssetClass | null) =>
      assetClass === null
        ? t('assetClasses.other')
        : t(`assetClasses.${ASSET_CLASS_TRANSLATION_KEYS[assetClass] ?? 'other'}`),
    [t]
  )

  // "Bitcoin · Crypto" — shared by the master-list rows and the venue-pane
  // header so the two never drift (e.g. one saying "Bitcoin Perpetual" while
  // the other says "Bitcoin"). Reads the precomputed `asset.assetClass`
  // (see groupByAsset) rather than rescanning `asset.instruments` — this is
  // called once per visible row on every render of the wizard (e.g. every
  // keystroke while typing a search query), so for a large options chain
  // that rescan was real, repeated, debounce-proof work.
  const describeAsset = useCallback(
    (asset: WizardAsset) =>
      asset.assetClass !== undefined
        ? `${asset.label} · ${localizedAssetClassLabel(asset.assetClass)}`
        : asset.label,
    [localizedAssetClassLabel]
  )

  // Venue logos for the master row's exchange stack — deduped and ordered by
  // SUPPORTED_EXCHANGE_LIST so the same asset always shows the same venues in
  // the same order, regardless of instrument catalog ordering.
  // Dedupe by the resolved ICON key, not by exchange — distinct exchanges can
  // share one icon (getExchangeIconKey: XKRX -> toss, OPRA -> databento), and an
  // asset on two of them would otherwise get that icon key twice in the same
  // row: duplicate logos, and a React key collision since WatchlistMasterRow
  // keys these by iconKey.
  const assetExchangeIconKeys = useCallback((asset: WizardAsset): string[] => {
    const present = new Set(asset.instruments.map((instrument) => instrument.exchange))
    const seenIconKeys = new Set<string>()
    return SUPPORTED_EXCHANGE_LIST.filter((exchange) => present.has(exchange)).flatMap(
      (exchange) => {
        const iconKey = getExchangeIconKey(exchange)
        if (!iconKey || seenIconKeys.has(iconKey)) return []
        seenIconKeys.add(iconKey)
        return [iconKey]
      }
    )
  }, [])

  const assetExchangeIconKeyMap = useMemo(
    () => new Map(masterAssets.map((asset) => [asset.id, assetExchangeIconKeys(asset)])),
    [assetExchangeIconKeys, masterAssets]
  )

  const exchangeMenuOptions = availableExchanges.map(({ exchange, label }) => {
    const iconKey = getExchangeIconKey(exchange)
    return {
      value: exchange,
      label,
      icon: iconKey ? (
        <ExchangeIcon exchange={iconKey} className="size-3.5 shrink-0 rounded-full" />
      ) : undefined,
    }
  })

  const filterSummary = <T,>(activeSet: Set<T>, labelOf: (value: T) => string): string => {
    if (activeSet.size === 0) return t('filterAllOption')
    if (activeSet.size === 1) return labelOf([...activeSet][0])
    return String(activeSet.size)
  }

  // 3. Methods and functions
  const toggleCollapsedDetailSection = useCallback(
    (key: string) => {
      setCollapsedDetailState((current) => {
        const sections = new Set(
          current.assetId === selectedAssetId ? current.sections : defaultCollapsedDetailSections
        )
        if (sections.has(key)) sections.delete(key)
        else sections.add(key)
        return { assetId: selectedAssetId, sections }
      })
    },
    [defaultCollapsedDetailSections, selectedAssetId]
  )

  const toggleInstrument = useCallback((instrument: Instrument) => {
    const key = watchlistEntryKey(instrument.exchange, instrument.symbol)
    setSelectedEntries((current) =>
      current.some((entry) => watchlistEntryKey(entry.exchange, entry.symbol) === key)
        ? current.filter((entry) => watchlistEntryKey(entry.exchange, entry.symbol) !== key)
        : [...current, { exchange: instrument.exchange, symbol: instrument.symbol }]
    )
  }, [])

  // Bulk toggle for a group header: fills in the rest when only some/none of the
  // group is selected, clears the whole group when it's fully selected. This
  // is one state update even for an option chain with thousands of contracts.
  const toggleGroupSelection = useCallback((groupInstruments: Instrument[]) => {
    setSelectedEntries((current) => {
      const currentKeys = watchlistEntrySet(current)
      const { selectable, allSelected } = groupSelectionState(
        groupInstruments,
        EMPTY_ENTRY_KEYS,
        currentKeys
      )
      if (allSelected) {
        const groupKeys = new Set(
          selectable.map((instrument) => watchlistEntryKey(instrument.exchange, instrument.symbol))
        )
        return current.filter(
          (entry) => !groupKeys.has(watchlistEntryKey(entry.exchange, entry.symbol))
        )
      }

      const next = [...current]
      for (const instrument of selectable) {
        const key = watchlistEntryKey(instrument.exchange, instrument.symbol)
        if (currentKeys.has(key)) continue
        currentKeys.add(key)
        next.push({ exchange: instrument.exchange, symbol: instrument.symbol })
      }
      return next
    })
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setQuery('')
        setSelectedIndex(0)
        setActivePane('master')
        setDetailIndex(-1)
        setCollapsedDetailState({ assetId: null, sections: new Set() })
        setSelectedEntries([])
        setExchangeFilters(new Set())
        setTypeFilters(new Set())
        setAssetClassFilter(null)
        setQuoteFilter(null)
        setBaseFilter(null)
        setCompareMode(null)
        setAtTokenActive(false)
        setContentReady(false)
      }
      onOpenChange(next)
    },
    [onOpenChange]
  )

  const handleAddToWatchlist = () => {
    if (props.mode === 'single') return
    const orderedEntries = [
      ...existingEntries.filter((entry) =>
        selectedKeys.has(watchlistEntryKey(entry.exchange, entry.symbol))
      ),
      ...selectedEntries.filter(
        (entry) => !existingKeys.has(watchlistEntryKey(entry.exchange, entry.symbol))
      ),
    ]
    setWatchlist(
      { watchlistId: props.watchlistId, orderedEntries },
      {
        onSuccess: () => handleOpenChange(false),
        onError: () => toast.error(t('failedToSave')),
      }
    )
  }

  const handleSelectInstrument = useCallback(
    (instrument: Instrument) => {
      if (chartCompare && compareMode) {
        chartCompare.onCompare(instrument, compareMode)
      } else if (onSelect) {
        const key = watchlistEntryKey(instrument.exchange, instrument.symbol)
        const cachedTicker = store.get(watchListTickerMapAtom)[key]
        const lastPrice =
          cachedTicker?.lastPrice ??
          wizardPriceStore.getSnapshot()[key]?.price ??
          cachedTicker?.markPrice ??
          cachedTicker?.indexPrice
        onSelect(instrument, lastPrice)
      }
      handleOpenChange(false)
    },
    [chartCompare, compareMode, handleOpenChange, onSelect, store]
  )

  const handleDismiss = () => {
    const searchInputFocused = searchBarRef.current?.isInputFocused() ?? false

    if (query && searchInputFocused) {
      setQuery('')
      return
    }
    if (activePane === 'detail') {
      setActivePane('master')
      setDetailIndex(-1)
      return
    }
    handleOpenChange(false)
  }

  const handleRowActivate = useCallback(
    (instrument: Instrument) => {
      if (isSingleMode) handleSelectInstrument(instrument)
      else toggleInstrument(instrument)
    },
    [handleSelectInstrument, isSingleMode, toggleInstrument]
  )

  // Only a keyboard move should scroll the list. Scrolling on a hover-driven
  // cursor change would slide rows out from under the pointer, which then
  // hovers a different row, which scrolls again.
  const cursorFromKeyboardRef = useRef(false)
  const ignoreSyntheticHoverRef = useRef(false)
  const syntheticHoverFrameRef = useRef<number | null>(null)

  const commitHover = useCallback((index: number) => {
    cursorFromKeyboardRef.current = false
    setSelectedIndex(index)
    setActivePane('master')
    setDetailIndex(-1)
  }, [])

  const hoverIntent = useHoverIntent(detailPaneRef, commitHover)

  const { cancel: cancelHoverIntent } = hoverIntent

  // Clicking (tapping) an asset row never picks anything by itself — it opens
  // that asset's venue/contract pane and moves the cursor into it, and the
  // user picks the exact market there. Side by side (desktop) that is the
  // hover preview made sticky; below md, where the panes take turns, it is
  // the step into the detail screen (with its back button). It used to commit
  // the asset's representative instrument straight away in single mode, so a
  // click meant to look at BTC's venues silently chose one (usually Deribit's).
  const handleAssetActivate = useCallback(
    (index: number) => {
      cancelHoverIntent()
      setSelectedIndex(index)
      if (!masterAssets[index]) return
      setActivePane('detail')
      setDetailIndex(0)
      setDetailEntryKey((key) => key + 1)
    },
    [cancelHoverIntent, masterAssets]
  )

  const applyView = (view: WizardView) => {
    setExchangeFilters(new Set(view.filters.exchanges))
    setTypeFilters(lockedTypeFilter ? new Set([lockedTypeFilter]) : new Set(view.filters.types))
    setAssetClassFilter(view.filters.assetClass)
    setQuoteFilter(view.filters.quote)
  }

  const toggleFilter = <T,>(setter: Dispatch<SetStateAction<Set<T>>>, value: T) => {
    setter((current) => {
      const next = new Set(current)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  const applySplitPercent = (nextPercent: number) => {
    const clamped = Math.min(MAX_SPLIT_PERCENT, Math.max(MIN_SPLIT_PERCENT, nextPercent))
    splitPercentRef.current = clamped
    if (masterPaneRef.current) masterPaneRef.current.style.width = `${clamped}%`
  }

  const handleSplitPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    splitDraggingRef.current = true
    setIsSplitDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleSplitPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!splitDraggingRef.current || !listContainerRef.current) return
    const bounds = listContainerRef.current.getBoundingClientRect()
    if (bounds.width <= 0) return
    applySplitPercent(((event.clientX - bounds.left) / bounds.width) * 100)
  }

  const finishSplitDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!splitDraggingRef.current) return
    splitDraggingRef.current = false
    setIsSplitDragging(false)
    setSplitPercent(splitPercentRef.current)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const handleSplitKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    let nextPercent = splitPercentRef.current
    if (event.key === 'ArrowLeft') nextPercent -= 2
    else if (event.key === 'ArrowRight') nextPercent += 2
    else if (event.key === 'Home') nextPercent = MIN_SPLIT_PERCENT
    else if (event.key === 'End') nextPercent = MAX_SPLIT_PERCENT
    else return
    event.preventDefault()
    applySplitPercent(nextPercent)
    setSplitPercent(splitPercentRef.current)
  }

  const moveCursor = (next: (current: number) => number) => {
    hoverIntent.cancel()
    cursorFromKeyboardRef.current = true
    setSelectedIndex(next)
    setDetailIndex(-1)
  }

  const { onRowEnter } = hoverIntent
  const handleHover = useCallback(
    (index: number, event: ReactMouseEvent<HTMLElement>) => {
      if (ignoreSyntheticHoverRef.current) return
      // A row's portalled popover (e.g. the market-status badge's) bubbles
      // its mouseenter through React to the row even while it sits over the
      // venue pane — that is not the pointer entering the row.
      if (!masterPaneRef.current?.contains(event.target as Node)) return
      onRowEnter(index, event)
    },
    [onRowEnter]
  )

  // 4. Effects
  useEffect(() => {
    if (!open) {
      setContentReady(false)
      return
    }
    const frame = requestAnimationFrame(() => setContentReady(true))
    return () => cancelAnimationFrame(frame)
  }, [open])

  const wasOpenRef = useRef(open)
  useEffect(() => {
    if (open && !wasOpenRef.current && !isSingleMode) {
      setSelectedEntries(existingEntries)
    }
    wasOpenRef.current = open
  }, [existingEntries, isSingleMode, open])

  useEffect(() => {
    if (!isSplitDragging) return
    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
    }
  }, [isSplitDragging])

  useEffect(() => {
    if (!open) return
    setTypeFilters(initialTypeFilter ? new Set([initialTypeFilter]) : new Set())
  }, [open, initialTypeFilter])

  const defaultCompareMode = compareModes[0]
  useEffect(() => {
    if (open) setCompareMode(compareRequiresMode ? defaultCompareMode : null)
  }, [open, compareRequiresMode, defaultCompareMode])

  const { initialQuery, initialBaseFilter } = props
  useEffect(() => {
    if (open && initialQuery) setQuery(initialQuery)
  }, [open, initialQuery])
  useEffect(() => {
    if (open && initialBaseFilter) setBaseFilter(initialBaseFilter.toUpperCase())
  }, [open, initialBaseFilter])

  // Every filter change re-homes the cursor at the top and hands the keyboard
  // back to the asset list, and bumps resultVersion so the master list's own
  // per-row animations (stagger fade-in / FLIP reorder — see the JSX below)
  // replay for the new result set. No blanket opacity fade here anymore —
  // that used to cover the reflow, but hiding the whole pane for a beat on
  // every keystroke/filter click fought with those animations instead of
  // reading as one, undermining the very transition it was meant to smooth.
  useEffect(() => {
    setSelectedIndex(0)
    setActivePane('master')
    setDetailIndex(-1)
    setResultVersion((version) => version + 1)
  }, [normalizedQuery, exchangeFilters, typeFilters, assetClassFilter, quoteFilter, baseFilter])

  // Only keyboard navigation scrolls the master list. Hover never scrolls,
  // so rows continue to follow the pointer immediately.
  useEffect(() => {
    if (clampedIndex < 0 || !cursorFromKeyboardRef.current) return
    cursorFromKeyboardRef.current = false
    ignoreSyntheticHoverRef.current = true
    if (syntheticHoverFrameRef.current !== null) {
      cancelAnimationFrame(syntheticHoverFrameRef.current)
    }
    if (isMasterListAnimated) {
      const selectedAssetForScroll = masterAssets[clampedIndex]
      if (selectedAssetForScroll) {
        masterPaneRef.current
          ?.querySelector(`[data-keyboard-row-key="${wizardGroupKey(selectedAssetForScroll)}"]`)
          ?.scrollIntoView({ block: 'nearest' })
      }
    } else {
      virtuosoRef.current?.scrollToIndex({ index: clampedIndex, align: 'center' })
    }
    syntheticHoverFrameRef.current = requestAnimationFrame(() => {
      ignoreSyntheticHoverRef.current = false
      syntheticHoverFrameRef.current = null
    })

    return () => {
      if (syntheticHoverFrameRef.current !== null) {
        cancelAnimationFrame(syntheticHoverFrameRef.current)
        syntheticHoverFrameRef.current = null
      }
      ignoreSyntheticHoverRef.current = false
    }
  }, [clampedIndex, isMasterListAnimated, masterAssets])

  // Scroll only when the cursor lands on a different row. detailRows (and so
  // detailNavTargets) is rebuilt on every price tick, and re-scrolling to the same
  // cursor each time yanked a mouse-wheel scroll back up to it.
  const lastScrolledDetailKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const target = detailNavTargets[clampedDetailIndex]
    if (!target || activePane !== 'detail') {
      lastScrolledDetailKeyRef.current = null
      return
    }
    if (lastScrolledDetailKeyRef.current === target.key) return
    lastScrolledDetailKeyRef.current = target.key
    detailVirtuosoRef.current?.scrollToIndex({ index: target.index, align: 'center' })
  }, [activePane, clampedDetailIndex, detailNavTargets])

  // Reassigning a ref every render is just a property write — cheap, and it
  // lets the handler always see this render's latest state/closures. The
  // effect below then only has to add/remove the actual `window` listener
  // when `open` changes, instead of tearing down and recreating a real
  // capture-phase global listener on every render (hover, keystroke, ...)
  // while the dialog is open.
  handleKeyDownRef.current = (event: KeyboardEvent) => {
    if (atTokenActive) return

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const step = event.key === 'ArrowDown' ? 1 : -1
      if (activePane === 'detail') {
        if (detailNavTargets.length === 0) return
        event.preventDefault()
        setDetailIndex((current) =>
          current < 0
            ? step > 0
              ? 0
              : detailNavTargets.length - 1
            : Math.min(Math.max(current + step, 0), detailNavTargets.length - 1)
        )
        return
      }
      if (masterAssets.length === 0) return
      event.preventDefault()
      moveCursor((current) => Math.min(Math.max(current + step, 0), masterAssets.length - 1))
      return
    }

    // At the end of the query, horizontal arrows cross panes. Keep native
    // caret movement when editing in the middle or extending a selection.
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
        return
      if (searchBarRef.current?.isInputFocused()) {
        const input = event.target
        if (
          !(input instanceof HTMLInputElement) ||
          input.selectionStart !== input.value.length ||
          input.selectionEnd !== input.value.length
        ) {
          return
        }
      }
      if (event.key === 'ArrowRight') {
        if (activePane !== 'master' || detailNavTargets.length === 0) return
        cancelHoverIntent()
        event.preventDefault()
        setActivePane('detail')
        setDetailIndex(0)
        setDetailEntryKey((key) => key + 1)
        return
      }
      if (activePane !== 'detail') return
      event.preventDefault()
      setActivePane('master')
      setDetailIndex(-1)
      return
    }

    if (event.key === 'Enter') {
      cancelHoverIntent()
      if (activePane === 'detail') {
        const target = detailNavTargets[clampedDetailIndex]
        if (!target) return
        event.preventDefault()
        handleRowActivate(target.instrument)
        return
      }
      if (!selectedAsset) return
      event.preventDefault()
      // A single-venue asset has nothing to choose between, so Enter
      // commits it; anything else steps into the venue pane.
      const soleInstrument =
        selectedAsset.instruments.length === 1 ? selectedAsset.instruments[0] : undefined
      if (isSingleMode && soleInstrument) {
        handleSelectInstrument(soleInstrument)
        return
      }
      handleAssetActivate(clampedIndex)
    }
  }

  useEffect(() => {
    if (!open) return
    const listener = (event: KeyboardEvent) => handleKeyDownRef.current(event)
    window.addEventListener('keydown', listener, true)
    return () => window.removeEventListener('keydown', listener, true)
  }, [open])

  // 5. JSX
  const venueCount = selectedAsset?.instruments.length ?? 0

  // Shared between the two master-list render modes below (Virtuoso for a
  // large browse-all catalog, a plain animated list for a short/filtered
  // result set) — same row content either way, just a different wrapper
  // animation around it.
  const renderMasterRowContent = (asset: CappedWizardAsset, index: number) => {
    const decoration = sections.decorations[asset.id]
    const description = describeAsset(asset)
    const representative = representativeInstrument(asset.instruments)
    const tickerKey = representative
      ? `${representative.exchange}:${representative.symbol}`
      : undefined
    const ticker = tickerKey ? masterTickerMap[tickerKey] : undefined
    const seeAllClass = decoration?.seeAll?.assetClass

    return (
      <>
        {decoration?.header ? (
          <WatchlistSectionHeader label={localizedAssetClassLabel(decoration.header.assetClass)} />
        ) : null}
        <WatchlistMasterRow
          asset={asset}
          index={index}
          label={asset.key}
          description={description}
          exchangeIconKeys={assetExchangeIconKeyMap.get(asset.id) ?? EMPTY_ICON_KEYS}
          ticker={ticker}
          marketExchange={representative?.exchange}
          selected={index === clampedIndex}
          singleMode={isSingleMode}
          searching={isSearching}
          query={debouncedQuery}
          showCollapsedOptionChain={showCollapsedOptionChain}
          existingKeys={EMPTY_ENTRY_KEYS}
          selectedKeys={selectedKeys}
          selectAllLabel={t('selectAll', { label: asset.key })}
          deselectAllLabel={t('deselectAll', { label: asset.key })}
          onToggleGroup={toggleGroupSelection}
          onHover={handleHover}
          onActivate={handleAssetActivate}
        />
        {/* Setting the class filter leaves one class standing, which turns
            section capping off by itself — there is no separate "expanded"
            mode to manage. */}
        {seeAllClass !== undefined ? (
          <WatchlistSeeAllRow
            label={t('seeAllCategory', { label: localizedAssetClassLabel(seeAllClass) })}
            onClick={() => setAssetClassFilter(seeAllClass)}
          />
        ) : null}
      </>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onEscapeKeyDown={(event) => {
          event.preventDefault()
          handleDismiss()
        }}
        className="border-foreground/10 text-foreground bg-popover/95 flex h-[75dvh] max-h-[760px] min-h-0 w-[min(94vw,1150px)] max-w-[1150px] flex-col gap-0 overflow-hidden border p-0 shadow-2xl backdrop-blur-2xl [&>button.absolute]:hidden"
      >
        <DialogTitle className="sr-only">
          {isSingleMode ? t('selectInstrument') : t('addInstruments')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {isSingleMode ? t('selectDescription') : t('addDescription')}
        </DialogDescription>

        <div className="border-foreground/10 flex shrink-0 items-center gap-3 border-b px-5 pt-2 pb-2">
          <Search className="text-foreground/40 size-4 shrink-0" aria-hidden="true" />
          <WatchlistWizardSearchBar
            ref={searchBarRef}
            query={query}
            onQueryChange={setQuery}
            placeholder={t('searchPlaceholder')}
            ariaLabel={t('searchInstruments')}
            exchangeFilters={exchangeFilters}
            typeFilters={typeFilters}
            assetClassFilters={assetClassFilterSet}
            onToggleExchange={(exchange) => toggleFilter(setExchangeFilters, exchange)}
            onToggleType={(type) => toggleFilter(setTypeFilters, type)}
            onToggleAssetClass={(assetClass) =>
              setAssetClassFilter((current) => (current === assetClass ? null : assetClass))
            }
            exchangeOptions={availableExchanges}
            typeOptions={searchBarTypeOptions}
            assetClassOptions={searchBarAssetClassOptions}
            onActiveTokenChange={setAtTokenActive}
            baseFilter={baseFilter}
            onClearBase={() => setBaseFilter(null)}
          />
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onPointerDown={(event) => event.preventDefault()}
            onClick={handleDismiss}
            aria-label={t('close')}
            className={cn(
              'text-foreground/45 hover:bg-foreground/5 hover:text-foreground p-2 transition-colors active:scale-95'
            )}
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* One chip row plus a tools cluster — every pixel spent on header
            chrome is a pixel of list the user doesn't get. */}
        <div className="border-foreground/10 flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2">
          <FilterRow
            options={availableAssetClasses.map((assetClass) => ({
              value: assetClass,
              label: localizedAssetClassLabel(assetClass),
            }))}
            activeSet={assetClassFilterSet}
            allLabel={t('filterAllOption')}
            onToggle={(assetClass) =>
              setAssetClassFilter((current) => (current === assetClass ? null : assetClass))
            }
            onReset={() => setAssetClassFilter(null)}
            collapsed={false}
          />
          {/* The side panel owns these from md up. */}
          <div className="flex shrink-0 items-center gap-1.5 md:hidden">
            {lockedExchangeFilter === undefined ? (
              <FilterMenu
                label={t('exchange')}
                summary={filterSummary(
                  exchangeFilters,
                  (exchange) =>
                    availableExchanges.find((option) => option.exchange === exchange)?.label ?? ''
                )}
                options={exchangeMenuOptions}
                activeSet={exchangeFilters}
                allLabel={t('filterAllOption')}
                onToggle={(exchange) => toggleFilter(setExchangeFilters, exchange)}
                onReset={() => setExchangeFilters(new Set())}
              />
            ) : null}
            {!lockedTypeFilter ? (
              <FilterMenu
                label={t('type')}
                summary={filterSummary(typeFilters, (type) => t(`marketTypeFilters.${type}`))}
                options={MARKET_TYPE_ORDER.map(({ key }) => ({
                  value: key,
                  label: t(`marketTypeFilters.${key}`),
                }))}
                activeSet={typeFilters}
                allLabel={t('filterAllOption')}
                onToggle={(type) => toggleFilter(setTypeFilters, type)}
                onReset={() => setTypeFilters(new Set())}
              />
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <WatchlistWizardSidePanel
            views={savedViews}
            activeViewId={activeViewId}
            onApplyView={applyView}
            exchangeOptions={lockedExchangeFilter === undefined ? exchangeMenuOptions : undefined}
            exchangeFilters={exchangeFilters}
            exchangeSummary={
              exchangeFilters.size === 0
                ? t('sidePanel.anyExchange')
                : exchangeFilters.size === 1
                  ? (availableExchanges.find(({ exchange }) => exchangeFilters.has(exchange))
                      ?.label ?? '')
                  : t('sidePanel.exchangeCount', { count: exchangeFilters.size })
            }
            onToggleExchange={(exchange) => toggleFilter(setExchangeFilters, exchange)}
            onResetExchanges={() => setExchangeFilters(new Set())}
            quoteOptions={quoteOptions}
            quoteFilter={quoteFilter}
            onQuoteChange={setQuoteFilter}
            typeOptions={
              lockedTypeFilter
                ? undefined
                : MARKET_TYPE_ORDER.map(({ key }) => ({
                    value: key,
                    label: t(`marketTypeFilters.${key}`),
                  }))
            }
            typeFilters={typeFilters}
            onToggleType={(type) => toggleFilter(setTypeFilters, type)}
            onResetTypes={() => setTypeFilters(new Set())}
            compare={
              chartCompare
                ? {
                    mode: compareMode,
                    modes: compareModes,
                    allowNone: !compareRequiresMode,
                    onModeChange: setCompareMode,
                  }
                : undefined
            }
          />
          <div ref={listContainerRef} className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
            <section
              ref={masterPaneRef}
              style={{ width: `${splitPercent}%` }}
              onPointerMove={(event) => {
                if (event.pointerType !== 'mouse') return
                // Moves over a row's portalled popover still bubble here through React.
                if (!masterPaneRef.current?.contains(event.target as Node)) hoverIntent.cancel()
                else hoverIntent.onListPointerMove(event)
              }}
              onPointerLeave={hoverIntent.cancel}
              className={cn(
                // Below md there is no room for two panes, so they take turns:
                // `w-full!` has to out-specify the inline split width. Coming back
                // from the venue step it slides in from the left (an animation replays
                // whenever an element leaves display:none; desktop never hides it).
                'min-h-0 flex-none max-md:w-full!',
                'max-md:animate-in max-md:fade-in-0 max-md:slide-in-from-left-6 motion-reduce:animate-none max-md:duration-200',
                activePane === 'detail' && 'max-md:hidden'
              )}
              aria-label={t('allMarkets')}
            >
              {(!contentReady || isLoading) && masterAssets.length === 0 ? (
                <div className="text-foreground/40 flex items-center justify-center gap-2 px-5 py-8 text-sm">
                  <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
                  {t('loadingMarkets')}
                </div>
              ) : null}
              {!isLoading && masterAssets.length === 0 ? (
                <div className="text-foreground/40 px-5 py-8 text-center text-sm">
                  {t('noMatchingMarkets', { query })}
                </div>
              ) : null}
              {masterAssets.length > 0 && isMasterListAnimated ? (
                // Short/filtered result set: a plain list so Framer Motion's
                // `layout` prop has real, continuous DOM positions to FLIP
                // between — this is what makes e.g. typing "hyp" slide HYPE
                // from row 4 up to row 1, and clearing the query slide it back
                // down, instead of just popping into place.
                //
                // `mode="popLayout"` matters here: the default mode keeps an
                // exiting row in normal document flow for the length of its
                // exit animation, so every surviving row below it stays
                // shoved down by that reserved space until the exit finishes
                // — reading as "the whole list drops" before it "catches up".
                // popLayout takes an exiting row out of flow (absolutely
                // positioned at its last spot) immediately, so survivors
                // layout-animate straight to their real new position — a
                // rearrangement instead of a push-down-then-recover.
                <OverlayScrollY className="h-full" scrollerClassName="relative h-full">
                  <AnimatePresence initial={false} mode="popLayout">
                    {masterAssets.map((asset, index) => (
                      <motion.div
                        key={asset.id}
                        layout="position"
                        // Enter/exit only ever fades — no explicit y offset.
                        // `layout` already drives position via its own
                        // transform, and pairing it with an explicit y in
                        // initial/animate fights that transform (the position
                        // animation kept getting clobbered back to instant),
                        // which is what made reordering look like a hard cut
                        // instead of a slide.
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          layout: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
                          opacity: { duration: 0.18 },
                        }}
                      >
                        {renderMasterRowContent(asset, index)}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </OverlayScrollY>
              ) : null}
              {masterAssets.length > 0 && !isMasterListAnimated ? (
                <div className="group/scroll-y relative h-full">
                  <Virtuoso
                    ref={virtuosoRef}
                    scrollerRef={(el) => setMasterScroller(el instanceof HTMLElement ? el : null)}
                    className="scrollbar-none h-full"
                    data={masterAssets}
                    computeItemKey={(_, asset) => asset.id}
                    increaseViewportBy={220}
                    rangeChanged={setMasterVisibleRange}
                    itemContent={(index, asset) => (
                      // A large browse-all catalog stays virtualized (see
                      // MASTER_LIST_ANIMATE_THRESHOLD) — Virtuoso snaps each row's
                      // position instantly rather than animating it, so instead
                      // of a FLIP reorder this just staggers each row's fade-in,
                      // replayed via the resultVersion key whenever the result
                      // set actually changes.
                      <motion.div
                        key={resultVersion}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.22,
                          ease: [0.16, 1, 0.3, 1],
                          delay:
                            Math.min(index, MASTER_ROW_STAGGER_MAX_INDEX) *
                            MASTER_ROW_STAGGER_DELAY_S,
                        }}
                      >
                        {renderMasterRowContent(asset, index)}
                      </motion.div>
                    )}
                  />
                  <OverlayThumbY scroller={masterScroller} />
                </div>
              ) : null}
            </section>

            <div
              role="separator"
              aria-label={t('resizePanelsAriaLabel')}
              aria-orientation="vertical"
              aria-valuemin={MIN_SPLIT_PERCENT}
              aria-valuemax={MAX_SPLIT_PERCENT}
              aria-valuenow={Math.round(splitPercent)}
              tabIndex={0}
              onPointerDown={handleSplitPointerDown}
              onPointerMove={handleSplitPointerMove}
              onPointerUp={finishSplitDrag}
              onPointerCancel={finishSplitDrag}
              onDoubleClick={() => {
                applySplitPercent(DEFAULT_SPLIT_PERCENT)
                setSplitPercent(DEFAULT_SPLIT_PERCENT)
              }}
              onKeyDown={handleSplitKeyDown}
              className={cn(
                'group relative z-20 hidden w-0 shrink-0 cursor-col-resize touch-none outline-none md:block',
                isSplitDragging && 'cursor-grabbing'
              )}
            >
              <span className="absolute inset-y-0 left-1/2 w-3 -translate-x-1/2" />
              <span
                className={cn(
                  'group-hover:bg-foreground/70 group-focus-visible:bg-foreground/70 bg-foreground/10 pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-[width,background-color] duration-150 group-hover:w-1 group-focus-visible:w-1',
                  isSplitDragging && 'bg-foreground w-1'
                )}
              />
            </div>

            <section
              ref={detailPaneRef}
              className={cn(
                'border-foreground/10 min-h-0 flex-1 flex-col border-l md:flex',
                activePane === 'detail' ? 'flex' : 'hidden',
                // Below md, stepping into an asset slides its venue screen in from the right.
                'max-md:animate-in max-md:fade-in-0 max-md:slide-in-from-right-6 motion-reduce:animate-none max-md:duration-200',
                activePane === 'detail' && 'bg-foreground/2'
              )}
              aria-label={selectedAsset?.key ?? t('selectMarketsToAdd')}
            >
              {selectedAsset ? (
                <div className="flex shrink-0 items-center gap-2.5 px-4 pt-3 pb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setActivePane('master')
                      setDetailIndex(-1)
                    }}
                    aria-label={t('allMarkets')}
                    className={cn(
                      // A full-size touch target: on a phone this is the step's way back.
                      'text-foreground/55 hover:bg-foreground/5 -ml-2 shrink-0 rounded-sm p-2 md:hidden'
                    )}
                  >
                    <ChevronLeft className="size-5" />
                  </button>
                  {selectedAsset.key === 'HYPE' ? (
                    <ExchangeIcon exchange="hyperliquid" className="size-8 shrink-0 rounded-full" />
                  ) : (
                    <CryptoIcon
                      symbol={selectedAsset.instruments[0]?.baseCurrency ?? selectedAsset.key}
                      iconTag={
                        selectedAsset.instruments.find((item) => item.enrichment?.iconTag)
                          ?.enrichment?.iconTag
                      }
                      className="size-8 shrink-0 rounded-full"
                      fallback={
                        <span className="flex size-full items-center justify-center bg-white/[0.07] text-[10px] font-semibold">
                          {selectedAsset.key.slice(0, 3)}
                        </span>
                      }
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">
                      {selectedAsset.key}
                    </span>
                    <span className="text-foreground/40 block truncate text-xs">
                      {describeAsset(selectedAsset)}
                    </span>
                  </span>
                  <span className="text-foreground/30 ml-auto shrink-0 text-[10px] tracking-tight">
                    {t('marketCount', { count: venueCount })}
                  </span>
                </div>
              ) : null}
              {selectedAsset && !isSingleMode ? (
                <div className="flex shrink-0 items-center gap-2 px-4 pb-2">
                  <WatchlistGroupSelectButton
                    instruments={
                      showCollapsedOptionChain
                        ? selectableAssetInstruments(selectedAsset.instruments, underlyingPrice)
                        : selectedAsset.instruments
                    }
                    label={selectedAsset.key}
                    size="sm"
                    existingKeys={EMPTY_ENTRY_KEYS}
                    selectedKeys={selectedKeys}
                    selectAllLabel={t('selectAll', { label: selectedAsset.key })}
                    deselectAllLabel={t('deselectAll', { label: selectedAsset.key })}
                    onToggle={toggleGroupSelection}
                  />
                  <span className="text-foreground/40 text-xs">
                    {t('selectAll', { label: selectedAsset.key })}
                  </span>
                </div>
              ) : null}
              <div className="min-h-0 flex-1">
                <WatchlistDetailPanel
                  activeGroup={selectedAsset}
                  assetOnlySelection={Boolean(assetOnlySelection)}
                  rows={detailRows}
                  virtuosoRef={detailVirtuosoRef}
                  collapsedSections={collapsedDetailSections}
                  singleMode={isSingleMode}
                  existingKeys={existingKeys}
                  selectedKeys={selectedKeys}
                  highlightedKey={highlightedKey}
                  searching={isSearching}
                  query={debouncedQuery}
                  pricesEnabled={open && Boolean(selectedAsset) && !assetOnlySelection}
                  priceResetKey={selectedAsset?.id ?? null}
                  entryKey={detailEntryKey}
                  exchangeFallbackLabel={t('exchange')}
                  emptyLabel={t('selectMarketsToAdd')}
                  marketCountLabel={(count) => t('marketCount', { count })}
                  selectAllLabel={(label) => t('selectAll', { label })}
                  deselectAllLabel={(label) => t('deselectAll', { label })}
                  onSelectInstrument={handleSelectInstrument}
                  onActivateInstrument={handleRowActivate}
                  onToggleGroup={toggleGroupSelection}
                  onToggleSection={toggleCollapsedDetailSection}
                />
              </div>
            </section>
          </div>
        </div>

        <div className="border-foreground/10 bg-foreground/3 flex shrink-0 items-center justify-between gap-4 border-t px-4 py-1">
          <div className="text-foreground/45 flex items-center gap-4 text-xs">
            <span className="hidden text-[10px] tracking-tight md:inline">{t('allMarkets')}</span>
            <span className="flex items-center gap-1.5">
              <span className="flex items-center gap-0.5">
                <kbd className={KEY_HINT_CLASS}>
                  <ArrowUpIcon className="size-3" aria-hidden="true" />
                </kbd>
                <kbd className={KEY_HINT_CLASS}>
                  <ArrowDownIcon className="size-3" aria-hidden="true" />
                </kbd>
              </span>
              Navigate
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className={KEY_HINT_CLASS}>
                <EnterKeyLabel />
              </kbd>
              Select
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className={KEY_HINT_CLASS}>
                <EscapeKeyLabel />
              </kbd>
              Close
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="text-foreground/45 text-xs tabular-nums">
              {t('resultCount', { count: resultCount })}
            </span>
            {!isSingleMode ? (
              <button
                type="button"
                disabled={!hasSelectionChanges || isPending}
                onClick={handleAddToWatchlist}
                className={cn(
                  'bg-emphasis text-emphasis-foreground rounded-md px-3 py-1.5 text-xs font-semibold transition-transform active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-35'
                )}
              >
                {isPending ? t('saving') : t('saveChanges')}
              </button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
